import React, { useState, useEffect } from 'react';
import { BookOpen } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import NavigationBar from '../../components/ui/NavigationBar';
import PremiumGate from '../../components/PremiumGate';
import LoadingState from '../../components/ui/LoadingState';
import LessonCard from './components/LessonCard';
import { fetchMicroLessons } from '../../services/microLessonsService';
import { drillsService } from '../../services/drillsService';

const MicroLessonsPage = () => {
  const { profile } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userSkillLevel, setUserSkillLevel] = useState(1);

  useEffect(() => {
    loadPageData();
  }, [profile?.id]);

  const loadPageData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Calculate user's skill level based on drill performance
      const skillLevel = await calculateUserSkillLevel();
      setUserSkillLevel(skillLevel);

      // Load and filter lessons by skill level
      const allLessons = await fetchMicroLessons();
      const filteredLessons = allLessons?.filter(
        lesson => lesson?.difficulty <= skillLevel
      );

      // Recommend by average user rating where available
      try {
        const lessonIds = filteredLessons?.map(l => l.id).filter(Boolean);
        if (lessonIds?.length > 0) {
          const { lessonRatingsService } = await import('../../services/lessonRatingsService');
          const averages = await lessonRatingsService.getAverageRatingsForLessons(lessonIds);
          // sort: highest average first, then difficulty asc
          filteredLessons.sort((a, b) => {
            const ra = averages?.[a.id] ?? null;
            const rb = averages?.[b.id] ?? null;
            if (ra === null && rb === null) return a.difficulty - b.difficulty;
            if (ra === null) return 1; // push nulls to end
            if (rb === null) return -1;
            if (rb === ra) return a.difficulty - b.difficulty;
            return rb - ra; // desc
          });
        }
      } catch (recErr) {
        console.warn('Recommendation fetch failed', recErr);
      }

      setLessons(filteredLessons);
    } catch (err) {
      setError(err?.message || 'Failed to load micro lessons');
      console.error('Error loading micro lessons:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateUserSkillLevel = async () => {
    try {
      if (!profile?.id) return 1;
      
      // Get user's drill history to calculate average score
      const userDrills = await drillsService?.getUserDrillHistory(100);
      
      if (!userDrills || userDrills?.length === 0) {
        return 1; // Beginner
      }

      const avgScore = userDrills?.reduce((sum, drill) => sum + (drill?.score || 0), 0) / userDrills?.length;
      
      // Map average score to skill level (1-5)
      if (avgScore < 40) return 1; // Beginner
      if (avgScore < 60) return 2; // Intermediate
      if (avgScore < 75) return 3; // Advanced
      if (avgScore < 90) return 4; // Expert
      return 5; // Master
    } catch (err) {
      console.error('Error calculating skill level:', err);
      return 1; // Default to beginner on error
    }
  };

  if (loading) {
    return <LoadingState message="Loading micro lessons..." />;
  }

  const getDifficultyLabel = (difficulty) => {
    const labels = {
      1: 'Beginner',
      2: 'Intermediate',
      3: 'Advanced',
      4: 'Expert',
      5: 'Master'
    };
    return labels?.[difficulty] || 'Unknown';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <NavigationBar />
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-8 h-8" />
            <h1 className="text-3xl font-bold">Micro Lessons</h1>
          </div>
          <p className="text-blue-100 max-w-2xl">
            Bite-sized sales training lessons tailored to your {getDifficultyLabel(userSkillLevel)} level
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <PremiumGate feature="microLessons" requiredPlan="Pro">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              <p className="font-medium">Error loading lessons</p>
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Results Count */}
          <div className="mb-4">
          <p className="text-gray-600">
            {lessons?.length} lesson{lessons?.length !== 1 ? 's' : ''} available at your level
          </p>
        </div>

        {/* Lessons Grid */}
        {lessons?.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No lessons available yet</h3>
            <p className="text-gray-500">
              Complete more drills to unlock lessons at your skill level
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lessons?.map((lesson) => (
              <LessonCard key={lesson?.id} lesson={lesson} />
            ))}
          </div>
        )}
        </PremiumGate>
      </div></div>
  );
};

export default MicroLessonsPage;